import { useEffect, useRef } from "react";
import classNames from "classnames";

import Tooltip from "@components/Tooltip";
import ArrowDownLite from "@components/Icons/ArrowDownLite";
import {
  ROMANIA_COUNTRY_CODE,
  countryCodeToFlag,
  getDisplayCallingCode,
  sanitizeNational,
  sanitizePhone,
  splitPastedPhone,
} from "@lib/phoneNumber";

// Composite phone control: a country prefix inside the field plus an input
// holding only the national number. The prefix is a real <select>, so it cannot
// be backspaced away, and the input never contains a "+".
const PhoneInput = (props) => {
  const {
    input,
    meta: { touched, error, active },
    validateOn,
    showErrors,
    inputClassName,
    labelClass,
    tooltip,
    countryCode,
    countryOptions,
    countryEditable,
    onCountryChange,
    ...inputProps
  } = props;

  const country = countryCode || ROMANIA_COUNTRY_CODE;

  const showInputErrorCondition =
    validateOn === "active" ? active || touched : touched;
  const hasError = Boolean(error) && showInputErrorCondition;

  // One country means nothing to choose, so the field renders as it did before
  // this control existed.
  const showCountryPicker = Boolean(
    countryEditable && countryOptions && countryOptions.length > 1
  );

  // The prefix without a select: on a DHL address the country belongs to the
  // address, so it is shown but not the customer's to change here.
  const showPrefix = showCountryPicker || !countryEditable;

  const fieldId = props.id || input.name;
  const selectId = `${fieldId}_country`;
  const errorId = `${fieldId}_error`;

  const inputRef = useRef(null);
  const caretRef = useRef(null);

  // Move a full number into (select, input) instead of leaving it in the input.
  const applyInternational = (raw) => {
    const pasted = splitPastedPhone(raw);

    if (!pasted) {
      return false;
    }

    const isOffered =
      !countryOptions ||
      countryOptions.some((option) => option.value === pasted.countryCode);

    // A country we do not offer must not land in the select: keep the current one
    // and let the validator explain. Same when the country is not editable - it
    // belongs to the address, and pasting a phone must not move the address.
    if (countryEditable && isOffered && pasted.countryCode !== country) {
      onCountryChange(pasted.countryCode);
    }

    input.onChange(pasted.national);

    return true;
  };

  const handleChange = (event) => {
    const element = event.target;
    const raw = element.value;

    // Catches autofill and a "00..." number typed by hand, which survives
    // digit-only sanitizing. "00" can never start a Romanian number.
    if (/^00\d/.test(sanitizePhone(raw)) && applyInternational(raw)) {
      return;
    }

    // Digits surviving left of the old caret, so it does not jump to the end when
    // a separator is stripped.
    const caret = element.selectionStart ?? raw.length;
    caretRef.current = sanitizeNational(raw.slice(0, caret)).length;

    input.onChange(sanitizeNational(raw));
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData?.getData("text") ?? "";

    // Not international: let the browser paste, handleChange sanitizes it.
    if (!splitPastedPhone(pasted)) {
      return;
    }

    event.preventDefault();
    applyInternational(pasted);
  };

  // useEffect, not useLayoutEffect: Next warns on the latter during SSR.
  useEffect(() => {
    const element = inputRef.current;

    if (
      element &&
      caretRef.current !== null &&
      document.activeElement === element
    ) {
      element.setSelectionRange(caretRef.current, caretRef.current);
    }

    caretRef.current = null;
  }, [input.value]);

  const telephoneInput = (
    <input
      {...input}
      {...inputProps}
      ref={inputRef}
      id={fieldId}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      autoCorrect="off"
      aria-invalid={hasError || undefined}
      aria-describedby={hasError ? errorId : undefined}
      onChange={handleChange}
      onPaste={handlePaste}
      className={classNames({
        [props.className]: props.className,
        [inputClassName]: inputClassName,
        "Form-phone-input": showPrefix,
        "error border-red-brand bg-red-bg": !showPrefix && hasError,
      })}
      data-test="PHONE_FIELD_NUMBER"
    />
  );

  return (
    <>
      {props.label && (
        <label
          htmlFor={fieldId}
          className={classNames("Form-label", {
            [labelClass]: labelClass,
            "is-required": props.required,
            disabled: props.disabled,
          })}
        >
          {typeof props.label === "string" ? (
            <span
              dangerouslySetInnerHTML={{ __html: props.label }}
              className="inline-block"
            ></span>
          ) : (
            props.label
          )}
          {tooltip && (
            <>
              {" "}
              <Tooltip content={tooltip} mode="click" />
            </>
          )}
        </label>
      )}

      {showPrefix ? (
        <>
          {/* A real label rather than aria-label: clickable, and it survives
              translation. */}
          {showCountryPicker && (
            <label htmlFor={selectId} className="sr-only">
              {props.prefixLabel}
            </label>
          )}

          <div
            className={classNames("Form-phone", {
              error: hasError,
              disabled: props.disabled,
            })}
            data-test="PHONE_FIELD"
          >
            <div className="Form-phone-prefix">
              {/* aria-hidden with a picker: the select already announces the
                  selected option, so the flag is decoration. Without one the
                  prefix is the only thing naming the country, so it has to be
                  readable. Joined through filter so a country without a flag does
                  not render a leading space. */}
              <span aria-hidden={showCountryPicker || undefined}>
                {[countryCodeToFlag(country), getDisplayCallingCode(country)]
                  .filter(Boolean)
                  .join(" ")}
              </span>

              {showCountryPicker && (
                <>
                  <ArrowDownLite width="9px" height="6px" aria-hidden="true" />

                  <select
                    id={selectId}
                    name={`${input.name}_country`}
                    className="Form-phone-select"
                    value={country}
                    disabled={props.disabled}
                    aria-invalid={hasError || undefined}
                    aria-describedby={hasError ? errorId : undefined}
                    autoComplete="tel-country-code"
                    onChange={(event) => onCountryChange(event.target.value)}
                    data-test="PHONE_FIELD_PREFIX"
                  >
                    {countryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {[
                          countryCodeToFlag(option.value),
                          option.label,
                          getDisplayCallingCode(option.value) &&
                            `(${getDisplayCallingCode(option.value)})`,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            {telephoneInput}
          </div>
        </>
      ) : (
        telephoneInput
      )}

      {showErrors && (
        <div
          id={errorId}
          className={classNames("text-red-brand text-sm mt-2px", {
            hidden: !hasError,
          })}
        >
          {error}
        </div>
      )}
    </>
  );
};

PhoneInput.defaultProps = {
  showErrors: true,
  validateOn: "touched",
  countryEditable: true,
  id: undefined,
  label: undefined,
  tooltip: null,
  required: undefined,
  disabled: undefined,
  placeholder: undefined,
  prefixLabel: "Prefix telefonic international",
};

export default PhoneInput;
